import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/Users/bradwestfall/Desktop/ReactTraining/Projects/monorepo/reacttraining.com/src/page-templates/BlogArticlePage.js";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <div>
  <p className="text-large align-center">
    If you're newer to React and some of the main ideas like how state and declarative approaches escape you, then 
    chances are you need to read this. For many newer React developers, terms like "declarative" might seem academic
    and so these concepts are skipped over. But they're actually the key to unlocking the more advanced knowledge down
    the road.
  </p>
    </div>
    <iframe width="100%" height="500" src="https://www.youtube.com/embed/Z2mQO0v7CwQ" title="YouTube video player" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe>
    <hr />
    <h3 {...{
      "id": "3-part-series"
    }}><a parentName="h3" {...{
        "href": "#3-part-series",
        "aria-hidden": "true",
        "tabIndex": -1
      }}><span parentName="a" {...{
          "className": "icon icon-link"
        }}></span></a>{`3-Part Series`}</h3>
    <ul>
      <li parentName="ul">{`⭐️ How is state related to the declarative approach in React?`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/blog/how-to-use-and-not-use-state"
        }}>{`How to use and not use state?`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "#"
        }}>{`How do props relate to state?`}</a>{` `}{`[Coming Soon]`}</li>
    </ul>
    <hr />
    <p>{`The "React way" of doing things is to re-render your component whenever you want to create a new set of instructions for updating the DOM. In other words, "UI is a `}<em parentName="p">{`function of`}</em>{` state" and when state changes, the UI will change accordingly with a re-render. In React, the term "re-render" simply means that our component function is called again to produce a new set of UI instructions based on the new state.`}</p>
    <p>{`Let's take a look at some `}<u>{`non`}</u>{`-React code to start to see how we can think in terms of a function getting called to produce new UI:`}</p>
    <div {...{
      "className": "gatsby-highlight",
      "data-language": "js"
    }}><pre parentName="div" {...{
        "className": "language-js"
      }}><code parentName="pre" {...{
          "className": "language-js"
        }}><span parentName="code" {...{
            "className": "token keyword"
          }}>{`function`}</span>{` `}<span parentName="code" {...{
            "className": "token function"
          }}>{`makeDiv`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token parameter"
          }}>{`x`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{`
  `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`return`}</span>{` `}<span parentName="code" {...{
            "className": "token template-string"
          }}><span parentName="span" {...{
              "className": "token template-punctuation string"
            }}>{`\``}</span><span parentName="span" {...{
              "className": "token string"
            }}>{`<div>`}</span><span parentName="span" {...{
              "className": "token interpolation"
            }}><span parentName="span" {...{
                "className": "token interpolation-punctuation punctuation"
              }}>{`\${`}</span>{`x`}<span parentName="span" {...{
                "className": "token interpolation-punctuation punctuation"
              }}>{`}`}</span></span><span parentName="span" {...{
              "className": "token string"
            }}>{`</div>`}</span><span parentName="span" {...{
              "className": "token template-punctuation string"
            }}>{`\``}</span></span>{`
`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span></code></pre></div>
    <p>{`You can see that we just need to call `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`makeDiv('hello')`}</code>{` if we want to get the div string back with "Hello" inside. If we want to think about the argument as being "stateful" and can change, imagine we have to call the function over and over again:`}</p>
    <div {...{
      "className": "gatsby-highlight",
      "data-language": "js"
    }}><pre parentName="div" {...{
        "className": "language-js"
      }}><code parentName="pre" {...{
          "className": "language-js"
        }}><span parentName="code" {...{
            "className": "token keyword"
          }}>{`let`}</span>{` message `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`=`}</span>{` `}<span parentName="code" {...{
            "className": "token string"
          }}>{`'Hello'`}</span>{`
`}<span parentName="code" {...{
            "className": "token function"
          }}>{`makeDiv`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span>{`message`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{`

message `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`=`}</span>{` `}<span parentName="code" {...{
            "className": "token string"
          }}>{`'World'`}</span>{` `}<span parentName="code" {...{
            "className": "token comment"
          }}>{`// state change`}</span>{`
`}<span parentName="code" {...{
            "className": "token function"
          }}>{`makeDiv`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span>{`message`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span></code></pre></div>
    <p>{`This is a good start for how to `}<em parentName="p">{`think`}</em>{` about React regarding interface changes due to state changes. In React, the difference from this is that the state is "local" inside the function instead of being passed as an argument. So, instead of us explicitly calling the function when the state changes and we want new UI, the component function call is more implicit when we change state with a setter function.`}</p>
    <p>{`The initial render of `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`MyComp`}</code>{` in this example will produce `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`<button>0</button>`}</code>{`. This is because the call to `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`useState`}</code>{` takes an initial state argument and then returns that to us and we called it `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`count`}</code>{`.`}</p>
    <div {...{
      "className": "gatsby-highlight",
      "data-language": "jsx"
    }}><pre parentName="div" {...{
        "className": "language-jsx"
      }}><code parentName="pre" {...{
          "className": "language-jsx"
        }}><span parentName="code" {...{
            "className": "token keyword"
          }}>{`function`}</span>{` `}<span parentName="code" {...{
            "className": "token function"
          }}>{`MyComp`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{`
  `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`const`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`[`}</span>{`count`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{` setCount`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`]`}</span>{` `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`=`}</span>{` `}<span parentName="code" {...{
            "className": "token function"
          }}>{`useState`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token number"
          }}>{`0`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{`
  `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`function`}</span>{` `}<span parentName="code" {...{
            "className": "token function"
          }}>{`add`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{`
    `}<span parentName="code" {...{
            "className": "token function"
          }}>{`setCount`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span>{`count `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`+`}</span>{` `}<span parentName="code" {...{
            "className": "token number"
          }}>{`1`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{`
  `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span>{`
  `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`return`}</span>{` `}<span parentName="code" {...{
            "className": "token tag"
          }}><span parentName="span" {...{
              "className": "token tag"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`<`}</span>{`button`}</span>{` `}<span parentName="span" {...{
              "className": "token attr-name"
            }}>{`onClick`}</span><span parentName="span" {...{
              "className": "token script language-javascript"
            }}><span parentName="span" {...{
                "className": "token script-punctuation punctuation"
              }}>{`=`}</span><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`{`}</span>{`add`}<span parentName="span" {...{
                "className": "token punctuation"
              }}>{`}`}</span></span><span parentName="span" {...{
              "className": "token punctuation"
            }}>{`>`}</span></span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{`count`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span><span parentName="code" {...{
            "className": "token tag"
          }}><span parentName="span" {...{
              "className": "token tag"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`</`}</span>{`button`}</span><span parentName="span" {...{
              "className": "token punctuation"
            }}>{`>`}</span></span>{`
`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span></code></pre></div>
    <p>{`Then when we click the button, here's the series of steps that takes place to update the UI:`}</p>
    <ol>
      <li parentName="ol">{`The `}<code parentName="li" {...{
          "className": "language-text"
        }}>{`onClick`}</code>{` calls the `}<code parentName="li" {...{
          "className": "language-text"
        }}>{`add`}</code>{` function which calls `}<code parentName="li" {...{
          "className": "language-text"
        }}>{`setCount`}</code>{`.`}</li>
      <li parentName="ol">{`Doing this will not mutate (change) the count variable for the rest of the scope of `}<code parentName="li" {...{
          "className": "language-text"
        }}>{`add`}</code>{`. Instead, calling `}<code parentName="li" {...{
          "className": "language-text"
        }}>{`setCount`}</code>{` will lead to React calling `}<code parentName="li" {...{
          "className": "language-text"
        }}>{`MyComp()`}</code>{` again (which is the re-render).`}</li>
      <li parentName="ol">{`In the subsequent call to `}<code parentName="li" {...{
          "className": "language-text"
        }}>{`MyComp`}</code>{`, calling `}<code parentName="li" {...{
          "className": "language-text"
        }}>{`useState`}</code>{` is the first thing that happens again and this time it returns out latest state where `}<code parentName="li" {...{
          "className": "language-text"
        }}>{`count`}</code>{` is now `}<code parentName="li" {...{
          "className": "language-text"
        }}>{`1`}</code>{`.`}</li>
      <li parentName="ol">{`The end result is we return `}<code parentName="li" {...{
          "className": "language-text"
        }}>{`<button>1</button>`}</code></li>
    </ol>
    <p>{`Notice that we don't think it terms of "Events change the UI". Instead, we think "Events lead to state changes and those state changes cause re-renders which lead to new instructions being given to React to update the UI".`}</p>
    <p>{`Had this been jQuery or vanilla JS where we `}<em parentName="p">{`do`}</em>{` think in terms of "Events change the UI", we might have:`}</p>
    <div {...{
      "className": "gatsby-highlight",
      "data-language": "js"
    }}><pre parentName="div" {...{
        "className": "language-js"
      }}><code parentName="pre" {...{
          "className": "language-js"
        }}><span parentName="code" {...{
            "className": "token keyword"
          }}>{`function`}</span>{` `}<span parentName="code" {...{
            "className": "token function"
          }}>{`add`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{`
  `}<span parentName="code" {...{
            "className": "token function"
          }}>{`$`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token string"
          }}>{`'.some-button'`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`.`}</span><span parentName="code" {...{
            "className": "token function"
          }}>{`html`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span>{`count `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`+`}</span>{` `}<span parentName="code" {...{
            "className": "token number"
          }}>{`1`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{`
`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span></code></pre></div>
    <p>{`The jQuery example uses the event to directly reach out to the DOM we means we need to think in terms of the event and `}<em parentName="p">{`how`}</em>{` that turns into a UI update. We would say this is "imperative" doing it the jQuery or vanilla JS way because we have to orchestrate exactly `}<em parentName="p">{`how`}</em>{` the UI change is implemented. Earlier when we called `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`makeDiv('hello')`}</code>{`, we would get a string returned and we would somehow figure out `}<em parentName="p">{`how`}</em>{` to turn that into a DOM node and `}<em parentName="p">{`how`}</em>{` to place it in the spot we wanted.`}</p>
    <p>{`All of that is "imperative" because of the `}<em parentName="p">{`how`}</em>{` parts.`}</p>
    <p>{`In React, we tend to prefer the "declarative" approach more where we can say:`}</p>
    <blockquote>
      <p parentName="blockquote">{`"This is my current state. React, here is an instruction for `}<em parentName="p">{`what`}</em>{` I want made. I don't care how it happens, you figure it out!"`}</p>
    </blockquote>
    <p>{`Then React makes it happen in the DOM.`}</p>
    <p>{`Later when the state changes:`}</p>
    <blockquote>
      <p parentName="blockquote">{`"Okay React, I have some new state, and here is my new JSX instruction for `}<em parentName="p">{`what`}</em>{` I want made. You figure out `}<em parentName="p">{`how`}</em>{` to update the DOM"`}</p>
    </blockquote>
    <p>{`React will make that DOM update `}<u>{`for you`}</u>{` when you return `}<u>{`what you want`}</u>{` with JSX. This is what we mean when we say "React allows us to update the DOM declaratively".`}</p>
    <h2 {...{
      "id": "but-what-if-i-need-update-something-imperatively"
    }}><a parentName="h2" {...{
        "href": "#but-what-if-i-need-update-something-imperatively",
        "aria-hidden": "true",
        "tabIndex": -1
      }}><span parentName="a" {...{
          "className": "icon icon-link"
        }}></span></a>{`But what if I need update something imperatively?`}</h2>
    <p>{`If you want, you can update the DOM directly (imperatively) in React too. We mostly use "refs" for this which gives us a "reference to the DOM", but that's out of scope for this series. In general we try to prefer the declarative approach first until it becomes obvious that we need to use an imperative approach. The reason is that with React, so many things happen for you for free when you embrace the declarative approach. Whereas with the imperative approach, you're mostly on your own.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      